import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';

/**
 * Agent Context with comprehensive analytics tracking
 * Manages agent creation flow and tracks all interactions
 */
const AgentContext = createContext(null);

export const useAgent = () => {
  const context = useContext(AgentContext);
  if (!context) {
    throw new Error('useAgent must be used within AgentProvider');
  }
  return context;
};

export const AgentProvider = ({ children }) => {
  // Always call useAnalytics hook unconditionally
  const { trackEvent, startTiming, endTiming } = useAnalytics();
  
  // Agent creation state
  const [currentStep, setCurrentStep] = useState(1);
  const [agentData, setAgentData] = useState({
    // Step 1 - Business Industry
    businessIndustry: '',
    businessIndustryOther: '',
    
    // Step 2 - Agent Type
    agentType: '',
    selectedTemplate: null,
    isCustomAgent: false,
    
    // Step 3 - Agent Details
    agentName: '',
    language: 'en',
    agentGoals: '',
    chatOnly: false,
    voiceSettings: {
      voiceId: '',
      stability: 0.5,
      similarityBoost: 0.8,
      style: 0.0,
      useSpeakerBoost: true
    },
    
    // Metadata
    creationStarted: null,
    creationCompleted: null,
    totalTimeSpent: 0,
    stepTimes: {},
    validationErrors: {},
    configComplexity: null
  });
  
  // Analytics tracking refs
  const creationStartTime = useRef(null);
  const stepStartTime = useRef({});
  const fieldModifications = useRef({});
  const templateInteractions = useRef([]);
  const voiceTestCount = useRef(0);
  
  // Add debugging
  console.log('AgentProvider initialized with currentStep:', currentStep);
  console.log('AgentProvider agentData:', agentData);
  
  /**
   * Get step name for analytics tracking
   */
  const getStepName = useCallback((stepNumber) => {
    const names = {
      1: 'business_industry',
      2: 'agent_type',
      3: 'agent_details'
    };
    return names[stepNumber] || `step_${stepNumber}`;
  }, []);
  
  /**
   * Initialize agent creation session
   */
  const initializeAgentCreation = useCallback((source = 'unknown') => {
    const now = Date.now();
    creationStartTime.current = now;
    
    setAgentData(prev => ({
      ...prev,
      creationStarted: new Date().toISOString()
    }));
    
    // Track creation started
    trackEvent('agent_creation_started', {
      source: source, // dashboard, onboarding, agents_page
      timestamp: new Date().toISOString(),
      user_agents_count: 0 // TODO: Get actual count
    });
    
    // Start timing
    startTiming('agent_creation', 'complete_flow');
  }, [trackEvent, startTiming]);
  
  /**
   * Track step navigation
   */
  const trackStepStart = useCallback((stepNumber, stepName) => {
    const now = Date.now();
    stepStartTime.current[stepNumber] = now;
    
    // Track step started
    trackEvent('agent_creation_step_started', {
      step_number: stepNumber,
      step_name: stepName,
      time_since_start: creationStartTime.current ? 
        Math.round((now - creationStartTime.current) / 1000) : 0
    });
    
    // Start timing for this step
    startTiming('agent_creation_step', stepName);
  }, [trackEvent, startTiming]);
  
  /**
   * Track step completion
   */
  const trackStepCompleted = useCallback((stepNumber, stepName) => {
    const now = Date.now();
    const timeSpent = stepStartTime.current[stepNumber] ? 
      Math.round((now - stepStartTime.current[stepNumber]) / 1000) : 0;
    
    // Update step times
    setAgentData(prev => ({
      ...prev,
      stepTimes: {
        ...prev.stepTimes,
        [stepName]: timeSpent
      }
    }));
    
    // Prepare step-specific data
    let stepData = {};
    
    if (stepNumber === 1) {
      stepData = {
        industry_selected: agentData.businessIndustry,
        is_other: agentData.businessIndustry === 'Other',
        custom_industry: agentData.businessIndustryOther
      };
    } else if (stepNumber === 2) {
      stepData = {
        agent_type: agentData.agentType,
        is_custom: agentData.isCustomAgent,
        template_selected: agentData.selectedTemplate,
        templates_viewed: templateInteractions.current.length
      };
    } else if (stepNumber === 3) {
      stepData = {
        agent_name_length: agentData.agentName.length,
        language: agentData.language,
        goals_length: agentData.agentGoals.length,
        chat_only: agentData.chatOnly,
        has_voice_config: !agentData.chatOnly && !!agentData.voiceSettings.voiceId
      };
    }
    
    // Track completion
    trackEvent('agent_creation_step_completed', {
      step_number: stepNumber,
      step_name: stepName,
      time_spent: timeSpent,
      ...stepData
    });
    
    // End timing for this step
    endTiming('agent_creation_step', stepName);
  }, [trackEvent, endTiming, agentData]);
  
  /**
   * Track field changes in agent details (Step 3)
   */
  const trackAgentDetailField = useCallback((fieldName, value, metadata = {}) => {
    // Track modification count
    if (!fieldModifications.current[fieldName]) {
      fieldModifications.current[fieldName] = 0;
    }
    fieldModifications.current[fieldName]++;
    
    // Track specific field events
    const eventMap = {
      agentName: 'agent_name_entered',
      language: 'agent_language_selected',
      agentGoals: 'agent_goals_entered',
      chatOnly: 'chat_only_toggled'
    };
    
    if (eventMap[fieldName]) {
      const properties = {
        field_name: fieldName,
        modification_count: fieldModifications.current[fieldName],
        ...metadata
      };
      
      // Add field-specific properties
      if (fieldName === 'agentName') {
        properties.name_length = value ? value.length : 0;
        properties.has_special_chars = /[^a-zA-Z0-9\s]/.test(value);
      } else if (fieldName === 'language') {
        properties.language_code = value;
        properties.is_english = value === 'en';
      } else if (fieldName === 'agentGoals') {
        properties.goals_length = value ? value.length : 0;
        properties.word_count = value ? value.split(/\s+/).length : 0;
      } else if (fieldName === 'chatOnly') {
        properties.enabled = value;
        properties.previous_voice_tests = voiceTestCount.current;
      }
      
      trackEvent(eventMap[fieldName], properties);
    }
  }, [trackEvent]);
  
  /**
   * Track template interaction
   */
  const trackTemplateInteraction = useCallback((templateId, action, metadata = {}) => {
    // Record interaction
    templateInteractions.current.push({
      templateId,
      action,
      timestamp: Date.now()
    });
    
    // Track event
    trackEvent('agent_template_interaction', {
      template_id: templateId,
      action: action, // viewed, selected, deselected, configured
      interaction_number: templateInteractions.current.length,
      ...metadata
    });
  }, [trackEvent]);
  
  /**
   * Track voice configuration
   */
  const trackVoiceConfiguration = useCallback((voiceId, settings) => {
    trackEvent('agent_voice_configured', {
      voice_id: voiceId,
      stability: settings.stability,
      similarity_boost: settings.similarityBoost,
      style: settings.style,
      use_speaker_boost: settings.useSpeakerBoost,
      is_default_settings: settings.stability === 0.5 && settings.similarityBoost === 0.8
    });
  }, [trackEvent]);
  
  /**
   * Track voice test
   */
  const trackVoiceTest = useCallback((voiceId, testText, success = true) => {
    voiceTestCount.current++;
    
    trackEvent('agent_voice_tested', {
      voice_id: voiceId,
      test_number: voiceTestCount.current,
      text_length: testText ? testText.length : 0,
      success: success
    });
  }, [trackEvent]);
  
    // Helper functions - moved up to avoid initialization issues
  const calculateConfigComplexity = useCallback(() => {
    let complexity = 'simple';
    
    const factors = {
      customAgent: agentData.isCustomAgent,
      longGoals: agentData.agentGoals.length > 200,
      voiceConfigured: !agentData.chatOnly && !!agentData.voiceSettings.voiceId,
      nonDefaultVoiceSettings: agentData.voiceSettings.stability !== 0.5 ||
                               agentData.voiceSettings.similarityBoost !== 0.8,
      multipleLanguages: agentData.language !== 'en'
    };
    
    const complexityScore = Object.values(factors).filter(Boolean).length;
    
    if (complexityScore >= 3) {
      complexity = 'advanced';
    } else if (complexityScore >= 1) {
      complexity = 'intermediate';
    }
    
    return complexity;
  }, [agentData]);
  
  const getPartialConfiguration = useCallback(() => {
    return {
      has_industry: !!agentData.businessIndustry,
      has_type: !!agentData.agentType,
      has_name: !!agentData.agentName,
      has_goals: !!agentData.agentGoals,
      has_language: !!agentData.language,
      chat_only_set: agentData.chatOnly !== null
    };
  }, [agentData]);

  /**
   * Track agent creation completion
   */
   const trackAgentCreationCompleted = useCallback((agentId, creationPath) => {
     const now = Date.now();
     const totalTime = creationStartTime.current ? 
       Math.round((now - creationStartTime.current) / 1000) : 0;
     
     // Calculate configuration complexity
     const complexity = calculateConfigComplexity();
     
     // Update completion data
     setAgentData(prev => ({
       ...prev,
       creationCompleted: new Date().toISOString(),
       totalTimeSpent: totalTime,
       configComplexity: complexity
     }));
     
    // Track agent created event (matches Firebase setup)
    trackEvent('agent_created', {
      agent_id: agentId,
      agent_name: agentData.agentName,
      success: true,
      total_time: totalTime,
      creation_path: creationPath,
      step_times: agentData.stepTimes,
      config_complexity: complexity,
      is_custom: agentData.isCustomAgent,
      template_used: agentData.selectedTemplate,
      chat_only: agentData.chatOnly,
      voice_configured: !agentData.chatOnly && !!agentData.voiceSettings.voiceId,
      voice_tests_performed: voiceTestCount.current,
      field_modifications: Object.keys(fieldModifications.current).length,
      total_modifications: Object.values(fieldModifications.current)
        .reduce((sum, count) => sum + count, 0)
    });
    
    // Track first agent created event (matches Firebase setup)
    // Note: This would need to be determined based on user's agent count
    trackEvent('first_agent_created', {
      agent_id: agentId,
      is_first: true, // This should be determined based on actual user data
      agent_name: agentData.agentName,
      success: true,
      total_time: totalTime,
      creation_path: creationPath
    });
    
    // Track conversion
    trackEvent('conversion', {
      conversion_type: 'agent_created',
      conversion_value: agentId,
      source: creationPath
    });
     
     // End timing
     endTiming('agent_creation', 'complete_flow');
     }, [trackEvent, endTiming, agentData, calculateConfigComplexity]);

  /**
   * Track agent creation abandonment
   */
   const trackAgentCreationAbandoned = useCallback((lastStep, reason) => {
     const now = Date.now();
     const totalTime = creationStartTime.current ? 
       Math.round((now - creationStartTime.current) / 1000) : 0;
     
     trackEvent('agent_creation_abandoned', {
       last_step: lastStep,
       reason: reason,
       total_time: totalTime,
       completed_steps: Object.keys(agentData.stepTimes).length,
       partial_config: getPartialConfiguration(),
       templates_viewed: templateInteractions.current.length,
       voice_tests_performed: voiceTestCount.current
     });
   }, [trackEvent, agentData.stepTimes, getPartialConfiguration]);
   
   /**
    * Track validation error
    */
   const trackValidationError = useCallback((stepNumber, fieldName, errorType) => {
     trackEvent('agent_creation_validation_error', {
       step_number: stepNumber,
       field_name: fieldName,
       error_type: errorType
     });
   }, [trackEvent]);
  
  // Navigation functions
  const goToNextStep = useCallback(() => {
    if (currentStep < 3) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      trackStepStart(nextStep, getStepName(nextStep));
    }
  }, [currentStep, trackStepStart, getStepName]);
  
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      trackStepStart(prevStep, getStepName(prevStep));
    }
  }, [currentStep, trackStepStart, getStepName]);
  
  // Data update function
  const updateAgentData = useCallback((data) => {
    setAgentData(prev => ({ ...prev, ...data }));
  }, []);
  
  const value = {
    // State
    currentStep,
    agentData,
    
    // Navigation
    goToNextStep,
    goToPreviousStep,
    setCurrentStep,
    
    // Data management
    updateAgentData,
    
    // Analytics tracking
    initializeAgentCreation,
    trackStepStart,
    trackStepCompleted,
    trackAgentDetailField,
    trackTemplateInteraction,
    trackVoiceConfiguration,
    trackVoiceTest,
    trackAgentCreationCompleted,
    trackAgentCreationAbandoned,
    trackValidationError
  };
  
  return (
    <AgentContext.Provider value={value}>
      {children}
    </AgentContext.Provider>
  );
};